import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  LayoutDashboard,
  FileText,
  Users,
  Building2,
  Receipt,
  Settings,
  LogOut,
  Menu,
  X,
  Search,
  ChevronDown,
  MessageSquare,
  Upload,
  Send
} from 'lucide-react';
import { toast } from 'sonner';
import { FeedbackDialog } from '@/components/FeedbackDialog';

const TrialBanner = ({ business }) => {
  if (!business?.is_trial || !business?.trial_ends_at) return null;
  const days = Math.max(0, Math.ceil((new Date(business.trial_ends_at) - new Date()) / 86400000));
  return (
    <div className="mx-4 lg:mx-8 mt-4 px-4 py-3 rounded-lg bg-[#EF9F27]/10 border border-[#EF9F27]/30 text-sm text-stone-800 flex items-center justify-between gap-4" data-testid="trial-banner">
      <span>
        <strong>Growth plan free trial</strong> - {days} day{days === 1 ? '' : 's'} left. After that your account moves to the Free plan; your data stays safe.
      </span>
      <span className="font-mono text-[#b8740f] font-semibold" data-testid="trial-days-left">{days}d</span>
    </div>
  );
};

// Minimal markdown: **bold**, bullet lists, headings
const renderInline = (line) => line.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
  part.startsWith('**') && part.endsWith('**') ? <strong key={i}>{part.slice(2, -2)}</strong> : part
);

const ChatText = ({ text }) => (
  <div className="text-sm space-y-1">
    {text.split('\n').map((raw, i) => {
      const line = raw.trimEnd();
      if (!line.trim()) return <div key={i} className="h-1" />;
      const bullet = line.match(/^\s*[-*•]\s+(.*)/);
      if (bullet) return <p key={i} className="pl-4 relative before:content-['•'] before:absolute before:left-1">{renderInline(bullet[1])}</p>;
      const heading = line.match(/^#{1,6}\s+(.*)/);
      if (heading) return <p key={i} className="font-semibold">{renderInline(heading[1])}</p>;
      return <p key={i}>{renderInline(line)}</p>;
    })}
  </div>
);
import axios from 'axios';
import { LakshLogo, LakshMark } from '@/components/LakshLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
  { icon: FileText, label: 'Invoices', path: '/invoices' },
  { icon: Users, label: 'Customers', path: '/customers' },
  { icon: Building2, label: 'Vendors', path: '/vendors' },
  { icon: Receipt, label: 'Expenses', path: '/expenses' },
  { icon: Upload, label: 'Migrate', path: '/migrate' },
  { icon: Settings, label: 'Settings', path: '/settings' },
];

export const DashboardLayout = ({ children }) => {
  const { user, business, logout, token } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSessionId, setChatSessionId] = useState(null);

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const sendChatMessage = async () => {
    if (!chatMessage.trim()) return;
    
    const userMsg = chatMessage;
    setChatMessage('');
    setChatHistory(prev => [...prev, { role: 'user', content: userMsg }]);
    setChatLoading(true);
    
    try {
      const response = await axios.post(
        `${API}/copilot/chat`,
        { message: userMsg, session_id: chatSessionId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setChatSessionId(response.data.session_id);
      setChatHistory(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      toast.error('Failed to get response from Arya');
      setChatHistory(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again.' 
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-100">
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="p-4 border-b border-stone-200">
          <Link to="/dashboard" className="flex items-center">
            <LakshLogo height={32} variant="light" />
          </Link>
        </div>
        
        <ScrollArea className="flex-1 py-4">
          <nav className="px-3 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-link ${location.pathname === item.path ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className="w-5 h-5" strokeWidth={1.5} />
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>
        </ScrollArea>

        <div className="p-4 border-t border-stone-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#EF9F27]/20 rounded-full flex items-center justify-center">
              <span className="text-[#EF9F27] font-semibold">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-stone-900 truncate">{user?.name}</p>
              <p className="text-xs text-stone-500 truncate">{business?.name}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="main-content">
        {/* Top Header */}
        <header className="sticky top-0 z-30 glass-header px-4 lg:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button 
              className="lg:hidden p-2 hover:bg-stone-100 rounded-md"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            
            <div className="flex items-center gap-2 bg-stone-100 rounded-md px-3 py-2 w-32 sm:w-64 min-w-0">
              <Search className="w-4 h-4 text-stone-400" />
              <input
                type="text"
                placeholder="Search invoices..."
                defaultValue={new URLSearchParams(location.search).get('q') || ''}
                data-testid="header-search-input"
                className="bg-transparent border-none outline-none text-sm flex-1"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.currentTarget.value.trim()) navigate(`/invoices?q=${encodeURIComponent(e.currentTarget.value.trim())}`);
                }}
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span className="hidden md:inline px-2 py-0.5 rounded-full text-xs font-semibold bg-[#EF9F27]/15 text-[#b8740f]" data-testid="beta-badge">BETA</span>
            <FeedbackDialog portal="user" token={token} triggerClassName="px-3 py-2 rounded-md text-stone-600 hover:bg-stone-100 hover:text-stone-900" />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 p-2 hover:bg-stone-100 rounded-md">
                  <div className="w-8 h-8 bg-[#1D9E75] rounded-full flex items-center justify-center ring-2 ring-[#1D9E75]/20" data-testid="header-avatar">
                    <span className="text-white font-semibold text-sm">
                      {user?.name?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </div>
                  <ChevronDown className="w-4 h-4 text-stone-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-stone-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <TrialBanner business={business} />

        {/* Page Content */}
        <main className="p-4 lg:p-8">
          {children}
        </main>
      </div>

      {/* Chat Widget - Arya */}
      <div className="chat-widget">
        {chatOpen && (
          <div className="chat-panel" data-testid="chat-panel">
            <div className="p-4 bg-[#1D9E75] text-white">
              <h3 className="font-semibold flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Arya - Compliance Co-Pilot
              </h3>
              <p className="text-xs text-emerald-100">Ask me anything about GST, TDS, or compliance</p>
            </div>
            
            <ScrollArea className="h-80 p-4">
              {chatHistory.length === 0 ? (
                <div className="text-center text-stone-500 text-sm py-8">
                  <p className="mb-2">Hi! I'm Arya, your AI compliance assistant.</p>
                  <p>Try asking:</p>
                  <ul className="mt-2 space-y-1 text-[#1D9E75]">
                    <li className="cursor-pointer hover:underline" onClick={() => setChatMessage('What is the GST rate for software services?')}>
                      "What is the GST rate for software services?"
                    </li>
                    <li className="cursor-pointer hover:underline" onClick={() => setChatMessage('When is the GSTR-1 due date?')}>
                      "When is the GSTR-1 due date?"
                    </li>
                    <li className="cursor-pointer hover:underline" onClick={() => setChatMessage('What TDS applies to contractor payments?')}>
                      "What TDS applies to contractor payments?"
                    </li>
                  </ul>
                </div>
              ) : (
                <div className="space-y-4">
                  {chatHistory.map((msg, i) => (
                    <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] rounded-lg p-3 ${
                        msg.role === 'user' 
                          ? 'bg-[#1D9E75] text-white' 
                          : 'bg-stone-100 text-stone-900'
                      }`}>
                        <ChatText text={msg.content} />
                      </div>
                    </div>
                  ))}
                  {chatLoading && (
                    <div className="flex justify-start">
                      <div className="bg-stone-100 rounded-lg p-3">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-stone-400 rounded-full animate-bounce"></span>
                          <span className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></span>
                          <span className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>
            
            <div className="p-4 border-t border-stone-200">
              <div className="flex gap-2">
                <Input
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  placeholder="Ask Arya..."
                  onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                  data-testid="chat-input"
                />
                <Button 
                  onClick={sendChatMessage}
                  disabled={chatLoading || !chatMessage.trim()}
                  className="bg-[#1D9E75] hover:bg-[#178a65]"
                  data-testid="chat-send-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
        
        <button 
          onClick={() => setChatOpen(!chatOpen)}
          className="chat-bubble"
          data-testid="chat-toggle-btn"
        >
          {chatOpen ? <X className="w-6 h-6" /> : <MessageSquare className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default DashboardLayout;
